import { Box, FormControlLabel, Paper, Switch } from "@mui/material";
import styles from "./MyTasks.module.scss";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { PaginatedDataTable } from "../../components/Table";
import { GridColDef } from "@mui/x-data-grid";
import BackButton from "../../components/BackButton";
import { getLabel } from "../../misc/utils.functions";
import moment from "moment";
import StatusComponent from "../../components/StatusComponent";
import { TaskPagedFilter } from "../../misc/pagedFilter";
import tasksService from "../../services/tasks.service";
import { TaskData } from "../../models/task.model";
import { useNavigate } from "react-router";

interface Props {
  showBack?: boolean;
}

const MyTasksList: React.FC<Props> = ({ showBack }) => {
  const { t } = useTranslation();
  const [tasks, setTasks] = useState<TaskData[]>([]);
  const [loading, setLoading] = useState(false);
  const title = t("myTasks");
  const navigate = useNavigate();

  const initFilter: TaskPagedFilter = {
    pageNumber: 1,
    pageSize: 5,
    sortField: "createdDate",
    sortDescending: true,
    showCompleted: false,
  };

  const [currentFilter, setCurrentFilter] =
    useState<TaskPagedFilter>(initFilter);
  const [totalRows, setTotalRows] = useState<number>(0);

  const columns: GridColDef[] = [
    {
      field: "ticketNumber",
      headerName: getLabel("ticketNumber"),
      sortable: true,
      width: 160,
    },
    {
      field: "title",
      headerName: getLabel("title"),
      sortable: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "createdBy",
      headerName: getLabel("createdBy"),
      sortable: true,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "task",
      headerName: getLabel("task"),
      sortable: true,
      flex: 1,
      minWidth: 150,
      renderCell: (params) => {
        return getLabel(`taskTypes.${params?.value}`);
      },
    },
    {
      field: "createdDate",
      headerName: getLabel("date"),
      sortable: true,
      width: 140,
      valueFormatter: (params) => moment(params?.value).format("MM/DD/YYYY"),
    },
    {
      field: "status",
      headerName: getLabel("status"),
      sortable: false,
      width: 180,
      renderCell: (params) => {
        return <StatusComponent text={params.row.status} />;
      },
    },
  ];

  useEffect(() => {
    setLoading(true);
    tasksService.getMyTasks(currentFilter).then((result) => {
      setTasks(result.list);
      setTotalRows(result.totalRows);
      setLoading(false);
    });
  }, [currentFilter]);

  const handlePageChange = (pageNumber: number) => {
    let filter = { ...currentFilter, pageNumber: pageNumber };
    setCurrentFilter(filter);
  };

  const handleSortChange = (field: string, sortDescending: boolean) => {
    let filter = {
      ...currentFilter,
      sortField: field,
      sortDescending: sortDescending,
    };
    setCurrentFilter(filter);
  };

  const handleRowClick = (row: TaskData) => {
    navigate(`${row.id}`, { relative: "route" });
  };

  const handleSwitchChange = (showCompleted: boolean) => {
    let filter = { ...currentFilter, showCompleted: showCompleted };
    setCurrentFilter(filter);
  };

  return (
    <Box className={styles.main}>
      <Paper
        elevation={3}
        className={styles.basic + " " + (showBack ? styles.showBack : "")}
      >
        <div className={styles.backContainer}>
          {showBack === true ? <BackButton text="back" to="/" /> : <></>}
        </div>
        <div className={styles.header}>
          <h3>{title}</h3>
          <div className={styles.filters}>
            <FormControlLabel
              control={<Switch onChange={(e, v) => handleSwitchChange(v)} />}
              label={t("viewCompletedTask")}
            />
          </div>
        </div>
        <PaginatedDataTable<TaskData>
          rows={tasks}
          totalRows={totalRows}
          initialPage={currentFilter.pageNumber - 1}
          pageSize={currentFilter.pageSize}
          columns={columns}
          handlePageChange={handlePageChange}
          handleRowClick={handleRowClick}
          handleSortChange={handleSortChange}
          emptyMessage={t("noRowsMessage", { entity: t("tasks") })}
          loading={loading}
        />
      </Paper>
    </Box>
  );
};

export default MyTasksList;
